/**
 * The sign-in experience, implemented from the `Lumen Sign-in v3 Neon` design.
 *
 * WHAT WAS IMPLEMENTED AND WHAT WAS NOT, stated up front because the mockup has six
 * screens and this has four:
 *
 *   01 Sign in        implemented, wired to GoTrue
 *   02 Authenticating implemented — it is the real in-flight state, not a timed fake
 *   05 Reset password implemented, wired to GoTrue's recovery email
 *   06 Set password   implemented, and it is MANDATORY — see below
 *
 *   03 Two-factor     implemented against GoTrue's REAL MFA API (challenge/verify), and
 *                     shown only when the account has a VERIFIED TOTP factor. With none
 *                     enrolled the sign-in is already fully elevated and the step is
 *                     skipped — a code box that accepted any six digits would teach the
 *                     operator that 2FA protects them when it does not.
 *   04 Workspace      implemented, and appears only when the account can reach more than
 *                     one organisation. Today `profiles.organisation_id` gives exactly
 *                     one, so it stays hidden; the gate is the data, not a feature flag.
 *   07 Locked         implemented, driven by GoTrue's real 429 rate-limit response. A
 *                     client-side attempt counter would be reset by a reload, so it would
 *                     inconvenience honest users and stop nobody.
 *
 * The forced password change IS real and is the reason it is kept: a seeded or invited
 * credential is SHARED until it is replaced, and while it is shared the audit trail cannot
 * attribute an action to a person — which is the whole point of having one.
 *
 * ONE SCREEN HERE IS NOT IN THE MOCKUP AT ALL: the code entry (`CodeScreen`). Every
 * transactional template renders a six-digit `{{ .Token }}` under "Button not working? Sign
 * in with this code instead" and nothing in the app accepted it — no `verifyOtp` call
 * existed anywhere in the tree. The recipient the fallback exists for, whose mail gateway
 * rewrote the link, could not complete their invitation. The chip stays in the email, so
 * the screen has to exist.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { BrandWordmark } from '@/components/lumen/BrandWordmark';
import { useAuth } from '@/lib/auth-context';
import { supabase, getRemember, setRemember } from '@/lib/supabase';
import { ApiError, api } from '@/lib/api/client';
import { useNow } from '@/lib/use-now';
import { toast } from '@/app/toasts';
import { Toasts } from '@/components/lumen/Toasts';
import {
  absorbRateLimit,
  cooldownRemaining,
  emailResendKey,
  releaseCooldown,
  startCooldown,
} from '@/modules/auth/resend-cooldown';
import { PowerRoomCaption, PowerRoomScene } from './PowerRoomScene';
import { SitePhoto } from './SitePhoto';
import { CodeScreen, type CodePurpose } from './screens/CodeScreen';
import { ConfirmScreen } from './screens/ConfirmScreen';
import { LockedScreen } from './screens/LockedScreen';
import { MfaScreen } from './screens/MfaScreen';
import { SetPasswordScreen } from './screens/SetPasswordScreen';
import './signin.css';

type Screen = 'signin' | 'busy' | 'mfa' | 'forgot' | 'sent' | 'locked' | 'code' | 'confirm';

/**
 * Why the sign-in frame is showing something other than the password form.
 *
 * Handed down by the auth gate, which is the only thing that can know: it reads the URL
 * this tab was opened with. See `modules/auth/auth-fragment.ts`.
 */
export interface SignInEntry {
  /**
   * Which code screen to open, or NULL when none of them applies.
   *
   * `parseAuthLink` recognises six link types and only three have a code screen. A failed
   * `signup`, `email_change` or `email` link used to fall through to a default of
   * `recovery`, so a broken email-change confirmation told the reader to "Enter your reset
   * code" — for a reset they never requested, from an email that contains no such code.
   * Null means: show the failure on the ordinary sign-in screen and stay there.
   */
  purpose: CodePurpose | null;
  /** The failure that sent the person here — an expired link, usually. */
  notice?: string | undefined;
  /**
   * The credential in the email is GONE, not merely unusable in link form.
   *
   * The distinction decides what the code screen may offer. A MALFORMED link — truncated by
   * a mail client, mangled by a scanner — leaves the six-digit code in the same email
   * perfectly valid, and offering it is the whole reason that screen exists. An EXPIRED or
   * already-spent link does not: GoTrue issues one credential per email and the code IS that
   * credential, so it died with the link. Asking someone to type it can only fail, and the
   * screen was doing exactly that with no other way forward on it.
   */
  expired?: boolean | undefined;
  /**
   * GoTrue's hashed token from an emailed link, NOT yet redeemed. Present, the frame opens on
   * the Continue step, and only that button exchanges it — see `screens/ConfirmScreen.tsx` for
   * the mail scanners that made this necessary. `purpose` says which kind of link it is.
   */
  confirm?: string | undefined;
}

const AMBER_GLOW = '0 6px 26px rgba(245,158,11,0.3)';

/**
 * The forced password change, in the sign-in frame.
 *
 * Separate entry point rather than a screen inside `LumenSignIn`, because it is reached
 * with a VALID session — the auth gate routes to it after sign-in succeeds. Folding it
 * into the sign-in state machine would mean that machine had to know about an
 * authenticated state, which is how a "skip" path eventually gets added by accident.
 */
export function LumenSetPassword({
  onDone,
  reason = 'issued',
}: {
  /**
   * Fired once the password is actually changed.
   *
   * The gate reaches this screen by two routes — the `must_change_password` flag, which
   * clears itself, and an invite link's `type=invite`, which does not. Without this the
   * second route would hold the operator on the set-password screen after they had already
   * set one.
   */
  onDone?: (() => void) | undefined;
  /**
   * Which of the two routes it was.
   *
   * The screen's argument for itself — "this password was issued to you, so it must be
   * replaced" — is TRUE of an invite and FALSE of a reset, where nobody issued anything and
   * the operator asked to be here. Printing it anyway would be a small reassuring falsehood
   * in a product whose whole claim is that it does not print those.
   */
  reason?: 'issued' | 'reset' | undefined;
}): ReactNode {
  const { user, changePassword, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Frame>
      <SetPasswordScreen
        reason={reason}
        email={user?.email ?? 'unknown'}
        busy={busy}
        error={error}
        onSignOut={() => void signOut()}
        onSubmit={(next) => {
          setBusy(true);
          setError(null);
          void changePassword(next).then(({ error: err }) => {
            if (err) setError(err);
            else onDone?.();
            setBusy(false);
          });
        }}
      />
    </Frame>
  );
}

export function LumenSignIn({
  entry,
  onIntent,
  onLinkSettled,
}: {
  /** Present when this tab was opened by an invitation or reset link. */
  entry?: SignInEntry | undefined;
  /**
   * Tells the gate that the session about to arrive came from a link, so an invited user
   * reaches the set-password screen even if the backend's metadata write failed.
   */
  onIntent?: ((intent: 'invite' | 'recovery' | null) => void) | undefined;
  /**
   * The emailed link has been dealt with — redeemed, refused, or set aside — so the gate may
   * stop holding this frame up over a session. See `Gate` in `App.tsx`.
   */
  onLinkSettled?: (() => void) | undefined;
} = {}): ReactNode {
  const { signIn } = useAuth();
  /* Held from the FIRST render: the gate stops passing the entry once the link is settled, and
     the step must not lose its token mid-press. */
  const [confirmLink] = useState(() =>
    entry?.confirm && entry.purpose ? { tokenHash: entry.confirm, purpose: entry.purpose } : null,
  );
  const [screen, setScreen] = useState<Screen>(
    confirmLink ? 'confirm' : entry?.purpose ? 'code' : 'signin',
  );
  const [codePurpose, setCodePurpose] = useState<CodePurpose>(entry?.purpose ?? 'recovery');
  /* The link's failure, and only while it is the reason we are on the code screen — a
     "your invitation expired" banner over a password-reset code entry is a non sequitur. */
  const [codeNotice, setCodeNotice] = useState<string | null>(entry?.notice ?? null);

  /*
   * A FAILURE WITH NO CODE SCREEN STILL HAS TO BE SAID OUT LOUD.
   *
   * When `purpose` is null the reader lands on the ordinary sign-in form, and without this
   * the only sign their link failed is that nothing happened — the fragment is scrubbed on
   * capture, so there is not even a broken URL left to look at. The toast is the same
   * channel every other sign-in failure uses.
   */
  useEffect(() => {
    if (entry && entry.purpose === null && entry.notice) toast.error(entry.notice);
    /* Once, on mount: `entry` is a module-level capture and never changes. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [codeExpired, setCodeExpired] = useState<boolean>(entry?.expired ?? false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  /** Kept apart from `error`, which belongs to the password form. */
  const [sendError, setSendError] = useState<string | null>(null);

  async function onSignin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setScreen('busy');

    const { error: err } = await signIn(email.trim(), password);

    if (err) {
      /*
       * GoTrue rate-limits by address and answers 429. Recognising it and showing the
       * lockout screen turns "Incorrect email or password" — which is not what happened
       * and sends the user to reset a password that was fine — into something actionable.
       */
      if (/too many|rate limit|429/i.test(err)) {
        const secs = /(\d+)\s*second/i.exec(err);
        setRetryAfter(secs?.[1] ? Number(secs[1]) : null);
        setScreen('locked');
        return;
      }
      setError(err);
      setScreen('signin');
      return;
    }

    /*
     * Password accepted. If the account carries a verified TOTP factor the session is
     * still AAL1 and must be elevated before it is useful, so the MFA step is entered
     * here rather than skipped.
     *
     * A failure listing factors is treated as "no factors": GoTrue is the authority on
     * whether elevation is required, and blocking a working sign-in on a metadata call
     * that failed would lock everyone out of a healthy system.
     */
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const totp = data?.totp?.find((f) => f.status === 'verified');
      if (totp) {
        setFactorId(totp.id);
        setScreen('mfa');
        return;
      }
    } catch {
      /* treated as no factors — see above */
    }

    /*
     * Otherwise nothing to do. A successful, fully-elevated sign-in changes the session
     * and the gate above this component swaps the whole tree, so this stays on `busy`
     * until it unmounts; setting a "done" state would race that unmount.
     */
  }

  /**
   * SEND THE RESET EMAIL — and stop claiming to have sent it when nothing was sent.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * WHAT THIS USED TO DO, AND WHY IT WAS WRONG
   * ───────────────────────────────────────────────────────────────────────────
   *
   * `if (err) console.warn(...)` and then `setScreen('sent')` unconditionally. Pressing
   * "Send it again" inside GoTrue's 60-second `SMTP_MAX_FREQUENCY` window returns 429, and
   * the screen answered "Check your inbox" for an email the server had just refused to
   * send. So did a dead gateway and a broken mailer.
   *
   * The account-enumeration argument for showing "sent" either way is still right, and is
   * kept — but it only covers the UNKNOWN ADDRESS, which the API answers 200 to anyway. It
   * never covered a transport failure: a 500 from the mailer and an unreachable stack
   * happen identically for an address that exists and one that does not, so surfacing them
   * leaks nothing and is the only way the operator learns to try again.
   *
   * A 429 is absorbed into the cooldown and never shown. GoTrue's limit only bit for an
   * address it found, so a 429 WAS a signal; the API counts every address alike, but the
   * screen still treats it as "already sent", which is the truth whenever it was.
   */
  async function sendReset(): Promise<boolean> {
    const address = email.trim();
    if (address.length === 0) return false;

    const key = emailResendKey(address);
    /* Belt to the disabled button's braces: a keyboard-driven resubmit reaches here. */
    if (cooldownRemaining(key) > 0) return false;

    setSendError(null);
    /* Started on the attempt, not the success — see resend-cooldown.ts for why the timer
       must look identical for an address that exists and one that does not. */
    startCooldown(key);

    /*
     * THE API SENDS IT, NOT GOTRUE (2026-10-16). Office 365 takes app-only tokens for SMTP now and
     * GoTrue v2.167.0 can log in to a relay only with a password, so `resetPasswordForEmail` had
     * no way to deliver anything. `POST /password-reset` sends the same mail an administrator's
     * reset sends, through the API's token login, and keeps GoTrue's answers: 200 for an unknown
     * address, 429 inside a minute worded as GoTrue worded it, a 5xx when the relay refuses.
     * Why not GoTrue's send-email hook: `lumen-be` `users/service.ts::requestPasswordReset`.
     * The link lands on the console's own accept screen, as every reset link does.
     */
    let err: { status: number | undefined; message: string } | null = null;
    try {
      await api.post<Record<string, never>>('/password-reset', { email: address });
    } catch (e) {
      err =
        e instanceof ApiError
          ? { status: e.status, message: e.message }
          : /* A fetch that never left the browser — `String` keeps the browser's own words
               ("TypeError: Failed to fetch"), which only the branch below reads, never the screen. */
            { status: undefined, message: String(e) };
    }

    if (!err) return true;

    if (absorbRateLimit(key, err.status, err.message)) {
      /* An email really had just been sent, so "check your inbox" is the truth. */
      return true;
    }

    const m = err.message.toLowerCase();
    if (
      m.includes('failed to fetch') || // Chrome
      m.includes('networkerror') || // Firefox
      m.includes('load failed') // Safari — its wording matches neither of the others, and
      // without this branch a Safari user with a dead stack was told the MAILER had failed
      // and then held for 60 seconds for a request that never left the browser.
    ) {
      /* Nothing left the browser, so nothing was sent and the wait is unearned. */
      releaseCooldown(key);
      setSendError(
        `Cannot reach the sign-in service. Nothing was sent — check your connection and try again.`,
      );
      return false;
    }

    /*
     * THE MAILER REFUSED, AND THE COOLDOWN MUST GO WITH IT.
     *
     * The cooldown starts on the ATTEMPT, so that the timer looks identical for an address
     * that exists and one that does not — which is the whole reason it is started early.
     * But a mailer failure (a 500 "Error sending recovery email", what a misconfigured relay
     * produces) left it running, and `onForgot`'s "already sent, show them the sent screen"
     * shortcut then fired on the SECOND press and rendered "Check your inbox. We sent a reset
     * link to …" for an email that was never sent. The one thing this screen must never do.
     *
     * Releasing it here costs the enumeration-timing property only in the case where the
     * mailer is broken for EVERY address, which tells an attacker nothing about any of them.
     */
    releaseCooldown(key);
    setSendError(
      'That email could not be sent. Try again in a moment, or ask your platform administrator to check email delivery.',
    );
    return false;
  }

  async function onForgot(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    /*
     * Already sent inside the window — they went back, came forward and pressed it again.
     * The email is in their inbox, so the screen that says so is the honest answer; before
     * this, `sendReset` refused and the form simply did nothing, which reads as broken.
     */
    const address = email.trim();
    if (address.length > 0 && cooldownRemaining(emailResendKey(address)) > 0) {
      setSendError(null);
      setScreen('sent');
      return;
    }

    if (await sendReset()) setScreen('sent');
  }

  return (
    <Frame>
      {screen === 'signin' && (
        <SignInScreen
          email={email}
          password={password}
          showPw={showPw}
          error={error}
          onEmail={setEmail}
          onPassword={setPassword}
          onToggle={() => setShowPw((v) => !v)}
          onSubmit={onSignin}
          onForgot={() => {
            setError(null);
            setScreen('forgot');
          }}
        />
      )}

      {screen === 'busy' && <BusyScreen />}

      {screen === 'mfa' && factorId && (
        <MfaScreen
          email={email}
          factorId={factorId}
          onRestart={() => {
            void supabase.auth.signOut();
            setPassword('');
            setFactorId(null);
            setScreen('signin');
          }}
        />
      )}

      {screen === 'locked' && (
        <LockedScreen
          email={email}
          retryAfterSeconds={retryAfter}
          onBack={() => {
            setRetryAfter(null);
            setScreen('signin');
          }}
        />
      )}

      {screen === 'forgot' && (
        <ForgotScreen
          email={email}
          error={sendError}
          onEmail={setEmail}
          onSubmit={onForgot}
          onBack={() => setScreen('signin')}
        />
      )}

      {screen === 'sent' && (
        <SentScreen
          email={email}
          error={sendError}
          onBack={() => setScreen('signin')}
          onResend={() => void sendReset()}
          onUseCode={() => {
            /* The reason the code exists: the link in the email did not survive the
               journey. Reachable from here, not only from an /accept-invite landing. */
            setCodePurpose('recovery');
            setCodeNotice(null);
            setScreen('code');
          }}
        />
      )}

      {screen === 'confirm' && confirmLink && (
        <ConfirmScreen
          purpose={confirmLink.purpose}
          tokenHash={confirmLink.tokenHash}
          onAttempt={() => onIntent?.(confirmLink.purpose === 'magiclink' ? null : confirmLink.purpose)}
          onRedeemed={() => onLinkSettled?.()}
          onRejected={() => onIntent?.(null)}
          /* The token is gone, so the code from the same email is too: the code screen's
             expired state is the honest answer, and its way forward (a new reset, or "ask your
             administrator") is the right one. */
          onSpent={() => {
            setCodePurpose(confirmLink.purpose);
            setCodeNotice(null);
            setCodeExpired(true);
            setScreen('code');
            onLinkSettled?.();
          }}
          onBack={() => {
            setScreen('signin');
            onLinkSettled?.();
          }}
        />
      )}

      {screen === 'code' && (
        <CodeScreen
          purpose={codePurpose}
          email={email}
          onEmail={setEmail}
          notice={codeNotice}
          expired={codeExpired}
          /* The only self-service way back. An invitation has none — the form that sends it
             belongs to an administrator — so the screen says so instead of pretending. */
          onRequestNew={
            codePurpose === 'recovery'
              ? () => {
                  setCodeExpired(false);
                  setCodeNotice(null);
                  void sendReset();
                }
              : undefined
          }
          /* Registered BEFORE the verification lands — see the prop's own note. */
          onAttempt={() => onIntent?.(codePurpose === 'magiclink' ? null : codePurpose)}
          onRejected={() => onIntent?.(null)}
          onBack={() => setScreen('signin')}
        />
      )}
    </Frame>
  );
}

/* ══════════════════════════════════════════════════════════════════ frame ══ */

function Frame({ children }: { children: ReactNode }): ReactNode {
  return (
    <div
      className="lm-signin"
      style={{
        ['--font-heading' as string]: "'Inter', system-ui, sans-serif",
        ['--font-body' as string]: "'Inter', system-ui, sans-serif",
        /* Height lives in signin.css as a `100vh` -> `100dvh` pair with `overflow-y: auto`;
           React cannot hold two values for one property and the fallback needs exactly that.
           See the note there for why the frame is its own scroll container. */
        background: '#05070d',
        color: '#fafafa',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/*
        * THE POWER ROOM, AND THE MINUTE IT IS BUILT FOR.
        *
        * One photograph from inside a data centre — a cold aisle between two rows of racks, the
        * ceiling's light panels, and at its end a glass wall onto a green field with the standby
        * set on the grass — where the solar site used to be: this workspace is the data centre (Hamzah,
        * 2026-10-20, "inside the room showcasing the server racks", "greeny fields trees not
        * desert style"). It must look photographic, never drawn.
        *
        * Over it, on a sixty-second loop, the one story the product watches for: the grid goes,
        * the ceiling lights drop, the racks keep running on the batteries, the standby set
        * starts outside, the grid comes back (`PowerRoomScene.tsx`, Hamzah's pick on
        * 2026-10-20). Every overlay sits on a real object in the photograph. Slow enough that it
        * is never the thing you are looking at, which is the entire brief for a background
        * behind a login form. The source, crops, sizes and timeline are in `signin.css`.
        *
        * `aria-hidden`: it carries mood, not information. A screen reader announcing a
        * decorative photograph before the email field is noise.
        */}
      <div className="lm-si-world" aria-hidden>
        <div className="lm-si-plate">
          <div className="lm-si-photo">
            <SitePhoto />
          </div>
          <PowerRoomScene />
        </div>
      </div>

      {/*
        * THE SCRIM, and it is not optional.
        *
        * Measured on the data-centre photograph this page carried before the solar one, white
        * body text over the lit racks fell to roughly 2:1 — unreadable, and unreadable in a way
        * that changed as the image moved, so it cannot be fixed by choosing a text colour. The
        * scrim is soft ramps only, never a box with an edge. See `signin.css`.
        */}
      <div className="lm-si-scrim" aria-hidden />

      {/* What the scene is showing this moment, in words, in the corner the stage leaves. */}
      <PowerRoomCaption />

      {/* Above the scene and the stage, in the corner this screen leaves empty. */}
      <Toasts />

      <div className="lm-si-stage">
        {/*
          * The wordmark sits over the scene rather than inside the card, so the brand
          * reads at full width and the card stays purely functional.
          */}
        <header className="lm-si-brandbar">
          {/*
            * 64, up from 42. The wordmark is the only brand element on a page whose right
            * half is a form, and at 42 it read as a favicon in the corner rather than as
            * the product's name. The height is a NUMBER rather than a CSS size because the
            * component derives its width from it to reserve the box before the artwork
            * decodes — sizing it in CSS would reintroduce the layout shift that guards
            * against.
            */}
          <BrandWordmark height={64} ink="light" />
        </header>

        <div className="lm-si-copy">
          <p className="lm-si-eyebrow">Power room monitoring</p>
          <h1 className="lm-si-h1">
            <span className="lm-si-line" style={{ animationDelay: '80ms' }}>Ready before</span>
            <span className="lm-si-line" style={{ animationDelay: '200ms' }}>the grid drops.</span>
          </h1>
          {/*
            * ONE SENTENCE BETWEEN THE HEADLINE AND THE ROTATOR.
            *
            * The headline is a claim and the rotator is a list of features; without
            * something between them the column jumps from slogan to specifics with nothing
            * saying what the product IS. This is the only static prose on the page and it
            * is the sentence a stranger reads to decide whether they are in the right
            * place. The power room's since 2026-10-20: this workspace is the data centre, and the
            * solar sentence that stood here described another workspace's sites.
            */}
          <p className="lm-si-lede">
            Thunder watches your power room live, from the grid and the standby sets through the
            UPS units, batteries and DC systems to the racks, puts each fault in plain words, and
            follows the fix through a work order.
          </p>
          <FeatureSlider />
        </div>

        {/*
          * THE CARD FLOATS, on the right, over the scene.
          *
          * Glass rather than a solid panel: the corridor stays faintly visible through it, so
          * the card reads as sitting IN the room rather than as a rectangle pasted over a
          * wallpaper. `backdrop-filter` is the whole effect, and it is applied to a fixed
          * element that never scrolls — blurring a scrolling surface is what makes this
          * technique expensive.
          */}
        <main className="lm-si-card">{children}</main>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════ screens ══ */

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';

function SignInScreen({
  email,
  password,
  showPw,
  error,
  onEmail,
  onPassword,
  onToggle,
  onSubmit,
  onForgot,
}: {
  email: string;
  password: string;
  showPw: boolean;
  error: string | null;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onToggle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgot: () => void;
}): ReactNode {
  const [caps, setCaps] = useState(false);
  const [remember, setRememberState] = useState(() => getRemember());
  const disabled = email.trim().length === 0 || password.length === 0;
  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Welcome back.</h1>
      <p style={subStyle}>Use the email address your organisation invited.</p>

      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 18, marginTop: 32 }}>
        <Field label="Email">
          <input
            className="lm-si-input"
            type="email"
            autoComplete="username"
            placeholder="you@operator.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </Field>

        <Field label="Password">
          <span style={{ position: 'relative', display: 'flex' }}>
            <input
              className="lm-si-input"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => onPassword(e.target.value)}
              /*
               * CAPS LOCK, read from the keyboard event rather than guessed.
               *
               * `getModifierState` is the only reliable source — there is no way to query
               * the lock from JavaScript, only to observe it during an event, which is why
               * this hangs off the field rather than living in component state somewhere.
               *
               * Worth the six lines: a password field masks its own contents, so caps lock
               * is the one typo a person cannot see themselves making, and the failure it
               * produces is indistinguishable from a wrong password.
               */
              onKeyUp={(e) => setCaps(e.getModifierState('CapsLock'))}
              onKeyDown={(e) => setCaps(e.getModifierState('CapsLock'))}
              onBlur={() => setCaps(false)}
              style={{ ...inputStyle, padding: '0 66px 0 13px' }}
            />
            <button
              type="button"
              onClick={onToggle}
              className="lm-si-pw"
              style={{
                position: 'absolute',
                right: 4,
                top: 4,
                bottom: 4,
                padding: '0 11px',
                border: 0,
                borderRadius: 7,
                background: 'none',
                font: 'inherit',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#36c5d6',
                cursor: 'pointer',
              }}
            >
              {showPw ? 'Hide' : 'Show'}
            </button>
          </span>
        </Field>

        {caps && (
          <p className="lm-si-caps" role="status" style={{ margin: '7px 0 0', fontSize: 11.5, color: '#fbbf24' }}>
            Caps Lock is on.
          </p>
        )}


        {/*
          * BOTH OF THESE BELONG ABOVE THE BUTTON, and for the same reason: they are
          * decisions about the sign-in you are about to make, not things to consider after
          * you have made it. "Forgot your password?" under the button is a link you reach
          * only after failing; above it, it is an alternative you can take instead.
          */}
        <div className="lm-si-row">
          <label className="lm-si-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => {
                setRememberState(e.target.checked);
                /*
                 * Written IMMEDIATELY, not on submit. The storage adapter reads this
                 * preference on every token write, and the write that matters happens
                 * inside signInWithPassword — so a value saved after the call would arrive
                 * one sign-in too late.
                 */
                setRemember(e.target.checked);
              }}
            />
            <span>Remember me</span>
          </label>

          <a
            href="#reset"
            className="lm-si-link"
            onClick={(e) => {
              e.preventDefault();
              onForgot();
            }}
          >
            Forgot your password?
          </a>
          {/*
            * The design shows "Two-factor required" here unconditionally. It is omitted
            * until the account actually has a factor — a permanent claim that 2FA is
            * required, on a deployment where it is not, is the kind of reassuring
            * falsehood a compliance product must not print.
            */}
        </div>

        <button type="submit" disabled={disabled} style={amberButton(disabled)} className="lm-si-amber">
          Sign in
        </button>
      </form>

      {/*
        * Legal and support, under the form.
        *
        * They are genuinely required — a product that processes an operator's identity owes
        * them a stated policy — and they are the one thing on this card that is not part of
        * signing in, so they sit outside the form and at the smallest legible size.
        *
        * Support is a `mailto:` rather than a route, because someone who cannot sign in
        * cannot reach a page behind the sign-in.
        */}
      <nav className="lm-si-legal" aria-label="Legal and support">
        <a className="lm-si-legal-link" href="https://thunderenergy.ai/legal/terms" target="_blank" rel="noreferrer">Terms</a>
        <span aria-hidden>·</span>
        <a className="lm-si-legal-link" href="/legal/privacy">Privacy Policy</a>
        <span aria-hidden>·</span>
        <a className="lm-si-legal-link" href="mailto:operations@thunderenergy.ai?subject=Thunder%20sign-in%20help">
          Support
        </a>
      </nav>

    </section>
  );
}

/**
 * The authenticating state.
 *
 * Shown for exactly as long as the request takes — it is not a timed animation. If the
 * network is fast this flashes, and that is correct: a deliberate minimum delay would be
 * inventing latency to look busy.
 */
function BusyScreen(): ReactNode {
  return (
    <section style={{ width: '100%', maxWidth: 400 }}>
      <svg width="42" height="42" viewBox="0 0 42 42" style={{ marginBottom: 26 }} aria-hidden="true">
        <circle cx="21" cy="21" r="17" stroke="#232936" strokeWidth="3" fill="none" />
        <circle
          cx="21"
          cy="21"
          r="17"
          stroke="#f59e0b"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeDasharray="28 80"
          style={{ transformOrigin: '21px 21px', animation: 'lm-spin 900ms linear infinite' }}
        />
      </svg>
      <h1 style={{ ...h1Style, fontSize: 'clamp(26px, 2.6vw, 35px)' }}>Signing you in.</h1>
      <p style={subStyle}>Checking your credentials and loading the sites you have access to.</p>
    </section>
  );
}

function ForgotScreen({
  email,
  error,
  onEmail,
  onSubmit,
  onBack,
}: {
  email: string;
  /** A transport or mailer failure. An unknown address never reaches here. */
  error: string | null;
  onEmail: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}): ReactNode {
  const disabled = email.trim().length === 0;
  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Reset your password.</h1>
      <p style={subStyle}>
        Enter the address you sign in with. Your password does not change until you open
        the link we send.
      </p>
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 18, marginTop: 32 }}>
        <Field label="Email">
          <input
            className="lm-si-input"
            type="email"
            autoComplete="username"
            placeholder="you@operator.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </Field>
        <button type="submit" disabled={disabled} style={amberButton(disabled)} className="lm-si-amber">
          Send reset link
        </button>
        <a
          href="#signin"
          className="lm-si-link"
          style={{ fontSize: 13 }}
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          Back to sign in
        </a>
      </form>
    </section>
  );
}

function SentScreen({
  email,
  error,
  onBack,
  onResend,
  onUseCode,
}: {
  email: string;
  error: string | null;
  onBack: () => void;
  onResend: () => void;
  onUseCode: () => void;
}): ReactNode {
  /*
   * A second hand, because this screen holds a countdown. `useNow` exists for exactly this
   * and is cheap; the alternative — an interval per screen — is what LockedScreen does and
   * is one clock too many for the same job.
   */
  const now = useNow(1000);
  const left = cooldownRemaining(emailResendKey(email), now);

  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Check your inbox.</h1>
      <p style={subStyle}>
        We sent a reset link to <span style={{ color: '#d4d4d8' }}>{email}</span>. If it
        does not arrive within a few minutes, check your spam folder, then send it again
        below.
      </p>


      <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 30 }}>
        <button type="button" onClick={onBack} className="lm-si-ghost" style={ghostButton}>
          Back to sign in
        </button>
        {/*
          * The wait is SHOWN, not just enforced. GoTrue refuses a second mail inside 60
          * seconds and this button used to fire straight into that 429 — so the remaining
          * time is the one thing the reader needs, and a dead link with no explanation is
          * what they used to get instead.
          */}
        {left > 0 ? (
          <span
            className="lm-num"
            style={{ fontSize: 13, color: '#71717a', fontFeatureSettings: "'tnum' 1" }}
          >
            You can send it again in {left}s
          </span>
        ) : (
          <a
            href="#resend"
            className="lm-si-link"
            style={{ fontSize: 13 }}
            onClick={(e) => {
              e.preventDefault();
              onResend();
            }}
          >
            Send it again
          </a>
        )}
      </div>

      {/*
        * The code fallback, reachable. Every template prints a six-digit code under "Button
        * not working?" — this is the screen that finally accepts it, and a recipient whose
        * link was rewritten by their own mail gateway has no other way through.
        */}
      <p style={{ fontSize: 13, lineHeight: 1.65, margin: '28px 0 0', color: '#71717a' }}>
        Link broken or rewritten by your mail system?{' '}
        <a
          href="#code"
          className="lm-si-link"
          onClick={(e) => {
            e.preventDefault();
            onUseCode();
          }}
        >
          Enter the code from the email
        </a>
        .
      </p>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════ atoms ══ */

const h1Style = {
  fontWeight: 600,
  fontSize: 'clamp(30px, 3vw, 41px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: '#fafafa',
} as const;

const subStyle = {
  fontSize: 15,
  lineHeight: 1.6,
  margin: '14px 0 0',
  color: '#a1a1aa',
} as const;

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: 46,
  padding: '0 13px',
  font: 'inherit',
  fontSize: 15,
  color: '#fafafa',
  background: '#12161f',
  border: '1px solid #283041',
  borderRadius: 10,
  outline: 'none',
};

const ghostButton = {
  minHeight: 46,
  paddingInline: 22,
  border: '1px solid #2f3646',
  borderRadius: 10,
  background: '#12161f',
  font: 'inherit',
  fontSize: 14.5,
  fontWeight: 500,
  color: '#e4e4e7',
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  flex: 'none',
};

function amberButton(disabled: boolean) {
  return {
    minHeight: 48,
    marginTop: 4,
    border: 0,
    borderRadius: 10,
    font: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: '0.01em',
    color: '#18181b',
    background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
    boxShadow: disabled ? 'none' : AMBER_GLOW,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  } as const;
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'grid', gap: 7 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.13em',
          textTransform: 'uppercase',
          color: '#8b8b95',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Raise the screen's error as a toast rather than a note inside the card.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT LEFT THE CARD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * An error printed inside the sign-in card competes with the thing it is about. It pushes
 * the button down as it appears, so a second click can land somewhere else; it sits below
 * the fold on a short window; and on the "Check your inbox" screen it appeared under a
 * heading saying the mail had been sent, which reads as a contradiction rather than a
 * correction.
 *
 * A toast sits in the corner, over nothing, and says when it will go. The sign-in screen
 * has no chrome in its top right, which is why they go there and the alarm stack does not.
 *
 * The `error` prop is kept rather than removed: the screens still own their error state and
 * one day may want it inline again (a field-level message, for instance, belongs beside the
 * field). What changed is where it is drawn.
 */
function useErrorToast(error: string | null): void {
  useEffect(() => {
    if (error !== null && error !== '') toast.error(error);
  }, [error]);
}


/**
 * The feature rotator under the headline.
 *
 * I ARGUED AGAINST A ROTATOR ON THIS PAGE and the owner asked for one, so here it is — but
 * built so the objection does not apply. The original design was a four-slide carousel of
 * ARCHITECTURES: diagrams, tier targets, a different composition every few seconds beside
 * the password field. This rotates TEXT inside a box exactly as tall as the tallest slide.
 * Nothing reflows, nothing moves in the periphery, and the form never shifts under the
 * cursor.
 *
 * ALL FIVE SLIDES ARE RENDERED, stacked in one grid cell, and only the current one is
 * visible. The box's height is therefore the tallest slide at whatever width the column has
 * — measured by the browser, at every width, every font size. It used to be a `min-height`
 * guessed per breakpoint, which was right for one width and left an empty band under the
 * ticks at the rest (Hamzah, 2026-10-13, at a 700px tablet: the left rule ran on into it).
 *
 * It also stops when you are using the page. Focus anything, hover the copy, or switch tabs
 * and it holds — a sentence changing while you read it is the actual annoyance, not the
 * rotation itself.
 *
 * WHY FIVE, AND WHY THESE FIVE. The owner asked for the most important features rather than
 * one-line claims, and since 2026-10-20 they are the five a POWER ROOM operator uses: this
 * workspace is the data centre, and the earlier five sold a solar plant's. Each names the
 * rail entry it lives under — Overview, Sites (the site's own board and Backup analytics),
 * Alarms — and none describes anything that does not ship: the power room board, grid
 * continuity, generator readiness, UPS and battery health, alarms worked to the fix. One
 * short sentence each, so a slide is read before it turns.
 */
const FEATURES: readonly { module: string; k: string; v: string }[] = [
  {
    module: 'Sites',
    k: 'Power room board',
    v: 'Every device by its job, with its state now and its open alarms, on one board.',
  },
  {
    module: 'Overview',
    k: 'Grid continuity',
    v: 'Each time the grid went and for how long, read at the transfer switch.',
  },
  {
    module: 'Sites',
    k: 'Generator readiness',
    v: 'Whether each standby set is in Auto and will start by itself, before you need it.',
  },
  {
    module: 'Overview',
    k: 'UPS and battery health',
    v: "The load on every UPS, and each battery's charge and health down to its weakest module.",
  },
  {
    module: 'Alarms',
    k: 'Alarms to work orders',
    v: 'Faults become named alarms, worked through to the fix with targets set by severity.',
  },
];

function FeatureSlider(): ReactNode {
  const [i, setI] = useState(0);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || held) return;
    /* 7.2s, up from 6.2: these slides carry a heading AND a sentence, and the old interval
       was tuned for a single line. A rotator that moves on before the copy is read is worse
       than no rotator. */
    const t = window.setInterval(() => setI((n) => (n + 1) % FEATURES.length), 7200);
    return () => window.clearInterval(t);
  }, [held]);

  useEffect(() => {
    const onVis = (): void => setHeld(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return (
    <div
      className="lm-si-feat"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      {/*
        * `aria-live="off"`: this is decorative rotation, and announcing a new feature every
        * seven seconds over a password field is hostile to a screen reader. The current slide
        * is in the accessibility tree and readable on demand; the hidden four are not, since
        * `visibility: hidden` takes them out of it.
        */}
      <div className="lm-si-feat-stack" aria-live="off">
        {FEATURES.map((f, n) => (
          <div key={f.k} className={n === i ? 'lm-si-feat-text lm-si-feat-on' : 'lm-si-feat-text'}>
            {/* The index is a figure and takes the number face; the group name is a word and
                takes the text face — the console's own split. */}
            <p className="lm-si-feat-tag">
              <span className="lm-si-feat-idx">{String(n + 1).padStart(2, '0')}</span>
              <span className="lm-si-feat-sep" aria-hidden>
                /
              </span>
              <span className="lm-si-feat-mod">{f.module}</span>
            </p>
            <h2 className="lm-si-feat-h">{f.k}</h2>
            <p className="lm-si-feat-p">{f.v}</p>
          </div>
        ))}
      </div>

      <div className="lm-si-ticks" role="tablist" aria-label="Features">
        {FEATURES.map((st, n) => (
          <button
            key={st.k}
            type="button"
            role="tab"
            aria-selected={n === i}
            aria-label={st.k}
            className={n === i ? 'lm-si-tick lm-si-tick-on' : 'lm-si-tick'}
            onClick={() => setI(n)}
          />
        ))}
      </div>
    </div>
  );
}
